import Phaser from 'phaser';
import { Battlefield } from '../battle/Battlefield';
import { Formation } from '../battle/Formation';
import { resolveBattleWinner, sideScore } from '../battle/CombatResolver';
import { TacticalAI } from '../battle/TacticalAI';
import { SceneKeys } from '../game/Constants';
import { GameState } from '../game/GameState';
import { ArmyManager } from '../systems/ArmyManager';
import { AudioManager } from '../systems/AudioManager';
import { DataRegistry } from '../systems/DataRegistry';
import { ModifierManager } from '../systems/ModifierManager';
import type { UnitData } from '../types/data';
import type { UnitStackState } from '../types/game';
import type { BattleInput, BattleOrderType, BattlePhase, BattleResult, BattleRole, BattleSide, FormationCombatProfile, FormationConfig } from '../types/battle';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';

interface ObjectivePressure { attacker: number; defender: number; }

export class BattleScene extends Phaser.Scene {
  private attackers: Formation[] = [];
  private defenders: Formation[] = [];
  private selected = new Set<Formation>();
  private phase: BattlePhase = 'deployment';
  private inputData?: BattleInput;
  private paused = false;
  private speed = 1;
  private elapsedMs = 0;
  private hudText?: Phaser.GameObjects.Text;
  private statusText?: Phaser.GameObjects.Text;
  private battlefield!: Battlefield;
  private defenderAI!: TacticalAI;
  private objectivePressure: ObjectivePressure = { attacker: 0, defender: 0 };
  private objectiveWinner?: BattleSide;
  private endReason = '';
  private withdrawalRequested = false;
  private resultShown = false;

  constructor() { super(SceneKeys.Battle); }

  init(data: BattleInput) {
    this.inputData = data;
    this.attackers = [];
    this.defenders = [];
    this.selected.clear();
    this.phase = 'deployment';
    this.paused = false;
    this.speed = 1;
    this.elapsedMs = 0;
    this.objectivePressure = { attacker: 0, defender: 0 };
    this.objectiveWinner = undefined;
    this.endReason = '';
    this.withdrawalRequested = false;
    this.resultShown = false;
  }

  create() {
    AudioManager.instance.playMusic(this, 'battle_javanese_land');
    this.input.mouse?.disableContextMenu();
    this.drawBattlefield();
    this.createFormations();
    this.createHud();
    this.bindInputs();
    this.defenderAI = new TacticalAI('defender', this.battlefield);
    this.selectInitialLine();
    this.refreshHud();
    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => this.renderBattleToText();
    exposed.advanceTime = (ms: number) => {
      const step = 1000 / 60;
      const steps = Math.max(1, Math.ceil(ms / step));
      for (let index = 0; index < steps; index++) this.stepBattle(Math.min(step, Math.max(0, ms - index * step)));
    };
  }

  override update(_time: number, delta: number) { this.stepBattle(delta); }

  private drawBattlefield() {
    const { width, height } = this.scale;
    const provinceId = this.inputData?.provinceId ?? GameState.instance.requireCampaign().selectedProvinceId ?? 'unknown';
    this.add.rectangle(width / 2, height / 2, width, height, VisualTheme.colors.ink, 1).setDepth(-100);
    this.battlefield = new Battlefield(provinceId, width, height);
    this.battlefield.draw(this);
    this.add.rectangle(width / 2, 56, width, 112, VisualTheme.colors.night, 0.96).setStrokeStyle(2, VisualTheme.colors.gold, 0.82).setDepth(1000);
    this.add.text(width / 2, 22, `Battle of ${DataRegistry.instance.provinces[provinceId]?.name ?? provinceId}`, { fontFamily: VisualTheme.fonts.display, fontSize: '29px', color: VisualTheme.text.title, stroke: '#140d08', strokeThickness: 3 }).setOrigin(0.5).setDepth(1001);
    this.statusText = this.add.text(width / 2, 61, `${this.battlefield.terrainName} battlefield • deploy formations, set facing and designate reserves`, { fontFamily: VisualTheme.fonts.body, fontSize: '14px', color: VisualTheme.text.accent }).setOrigin(0.5).setDepth(1001);
  }

  private createFormations() {
    const input = this.inputData;
    const attackerFactionId = input?.attackerFactionId ?? 'majapahit';
    const defenderFactionId = input?.defenderFactionId ?? 'demak';
    this.attackers = this.buildSide('attacker', attackerFactionId, input?.attackerArmyIds ?? []);
    this.defenders = this.buildSide('defender', defenderFactionId, input?.defenderArmyIds ?? []);
    if (!this.defenders.length) this.defenders = this.buildFallbackSide('defender', defenderFactionId);
    if (!this.attackers.length) this.attackers = this.buildFallbackSide('attacker', attackerFactionId);
  }

  private buildSide(side: BattleSide, factionId: string, armyIds: string[]) {
    const campaign = GameState.instance.campaign;
    if (!campaign) return [];
    const formations: Formation[] = [];
    const color = Phaser.Display.Color.HexStringToColor(DataRegistry.instance.factions[factionId]?.colors.primary ?? (side === 'attacker' ? '#2f6f45' : '#9b2d2d')).color;
    const stacks: Array<{ stack: UnitStackState; armyId: string }> = [];
    for (const armyId of armyIds) {
      const army = campaign.armies[armyId];
      if (!army) continue;
      for (const stack of army.units) stacks.push({ stack, armyId });
    }
    stacks.splice(12);
    stacks.forEach(({ stack }, index) => {
      const unit = DataRegistry.instance.units[stack.unitId];
      if (!unit) return;
      const profile = this.profileFor(unit);
      const point = this.deploymentPoint(side, index, stacks.length, profile.role);
      const config: FormationConfig = {
        id: `${side}_${index}_${stack.unitId}`,
        factionId,
        unitId: stack.unitId,
        side,
        x: point.x,
        y: point.y,
        soldierCount: Math.min(profile.role === 'elephant' ? 24 : 72, Math.max(profile.role === 'elephant' ? 12 : 28, Math.round(stack.strength / (profile.role === 'elephant' ? 2.5 : 2)))),
        color,
        facing: side === 'attacker' ? 0 : Math.PI,
        reserve: index >= Math.max(5, stacks.length - 2) || profile.role === 'artillery',
        profile,
      };
      const modifiers = ModifierManager.instance.forFaction(factionId);
      const gunpowderPower = ModifierManager.instance.hasGunpowderEdge(factionId, unit) ? modifiers.battleAttackMultiplier : 1;
      formations.push(new Formation(this, config, this.textureFor(factionId, stack.unitId), this.battlefield, modifiers.battleMoraleBonus + Math.floor(stack.experience / 10), gunpowderPower * (1 + stack.experience / 120)));
    });
    return formations;
  }

  private buildFallbackSide(side: BattleSide, factionId: string) {
    const faction = DataRegistry.instance.factions[factionId];
    const color = Phaser.Display.Color.HexStringToColor(faction?.colors.primary ?? (side === 'attacker' ? '#2f6f45' : '#9b2d2d')).color;
    const roster = faction?.roster.length ? faction.roster : ['levy_spearmen'];
    return Array.from({ length: 7 }, (_unused, index) => {
      const unitId = roster[index % roster.length];
      const unit = DataRegistry.instance.units[unitId] ?? this.fallbackUnit(unitId);
      const profile = this.profileFor(unit);
      const point = this.deploymentPoint(side, index, 7, profile.role);
      const config: FormationConfig = { id: `${side}_${index}_${unitId}`, factionId, unitId, side, x: point.x, y: point.y, soldierCount: profile.role === 'elephant' ? 16 : 48, color, facing: side === 'attacker' ? 0 : Math.PI, reserve: index >= 5 || profile.role === 'artillery', profile };
      const modifiers = ModifierManager.instance.forFaction(factionId);
      return new Formation(this, config, this.textureFor(factionId, unitId), this.battlefield, modifiers.battleMoraleBonus, 1);
    });
  }

  private createHud() {
    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height - 58, width, 116, VisualTheme.colors.night, 0.97).setStrokeStyle(2, VisualTheme.colors.gold, 0.9).setDepth(1000);
    this.hudText = this.add.text(24, height - 108, '', { fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.body, wordWrap: { width: 625 }, lineSpacing: 3 }).setDepth(1001);
    const controls: Array<[string, number, () => void, number]> = [
      ['Begin Battle', 720, () => this.beginBattle(), 170],
      ['Hold', 860, () => this.issueOrder('hold'), 94],
      ['Advance', 962, () => this.issueOrder('attack'), 104],
      ['Skirmish', 1074, () => this.issueOrder('skirmish'), 108],
      ['Charge', 1190, () => this.issueOrder('charge'), 98],
      ['Reserve', 1296, () => this.issueOrder('reserve'), 100],
      ['Withdraw', 1423, () => this.requestWithdrawal(), 140],
    ];
    controls.forEach(([label, x, action, buttonWidth]) => createTextButton(this, { label, x, y: height - 78, width: buttonWidth, height: 36, fontSize: 13, onClick: action }).setDepth(1001));
    createTextButton(this, { label: '⟲ Face', x: 930, y: height - 32, width: 110, height: 30, fontSize: 12, onClick: () => this.rotateSelection(-Math.PI / 8) }).setDepth(1001);
    createTextButton(this, { label: 'Face ⟳', x: 1048, y: height - 32, width: 110, height: 30, fontSize: 12, onClick: () => this.rotateSelection(Math.PI / 8) }).setDepth(1001);
    createTextButton(this, { label: 'Rally', x: 1166, y: height - 32, width: 100, height: 30, fontSize: 12, onClick: () => this.issueOrder('rally') }).setDepth(1001);
    createTextButton(this, { label: 'Pause', x: 1274, y: height - 32, width: 98, height: 30, fontSize: 12, onClick: () => { this.paused = !this.paused; } }).setDepth(1001);
    createTextButton(this, { label: '1×', x: 1362, y: height - 32, width: 62, height: 30, fontSize: 12, onClick: () => { this.speed = 1; } }).setDepth(1001);
    createTextButton(this, { label: '2×', x: 1430, y: height - 32, width: 62, height: 30, fontSize: 12, onClick: () => { this.speed = 2; } }).setDepth(1001);
    createTextButton(this, { label: '3×', x: 1498, y: height - 32, width: 62, height: 30, fontSize: 12, onClick: () => { this.speed = 3; } }).setDepth(1001);
    this.add.rectangle(width - 115, 165, 182, 114, VisualTheme.colors.night, 0.82).setStrokeStyle(2, VisualTheme.colors.gold, 0.72).setDepth(1000);
    this.add.text(width - 196, 111, 'Battle Overview', { fontFamily: VisualTheme.fonts.display, fontSize: '15px', color: VisualTheme.text.title }).setDepth(1001);
  }

  private bindInputs() {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.phase === 'result' || pointer.y > this.scale.height - 120) return;
      const worldX = pointer.worldX;
      const worldY = pointer.worldY;
      if (pointer.rightButtonDown()) {
        if (this.phase === 'deployment') {
          if (this.battlefield.insideDeployment('attacker', { x: worldX, y: worldY })) this.deploySelection(worldX, worldY);
          return;
        }
        const enemy = this.defenders.find((formation) => formation.alive && formation.boundsContains(worldX, worldY));
        if (enemy) this.issueOrder('attack', undefined, enemy);
        else this.issueOrder('move', { x: worldX, y: worldY });
        return;
      }
      const clicked = [...this.attackers, ...this.defenders].find((formation) => formation.alive && formation.boundsContains(worldX, worldY));
      const shift = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT).isDown ?? false;
      if (!shift) this.clearSelection();
      if (clicked && clicked.side === 'attacker') { clicked.setSelected(true); this.selected.add(clicked); }
      this.refreshHud();
    });
    this.input.keyboard?.on('keydown-SPACE', () => { this.paused = !this.paused; });
    this.input.keyboard?.on('keydown-H', () => this.issueOrder('hold'));
    this.input.keyboard?.on('keydown-A', () => this.issueOrder('attack'));
    this.input.keyboard?.on('keydown-K', () => this.issueOrder('skirmish'));
    this.input.keyboard?.on('keydown-C', () => this.issueOrder('charge'));
    this.input.keyboard?.on('keydown-B', () => this.issueOrder('reserve'));
    this.input.keyboard?.on('keydown-R', () => this.issueOrder('rally'));
    this.input.keyboard?.on('keydown-W', () => this.requestWithdrawal());
    this.input.keyboard?.on('keydown-Q', () => this.rotateSelection(-Math.PI / 8));
    this.input.keyboard?.on('keydown-E', () => this.rotateSelection(Math.PI / 8));
    this.input.keyboard?.on('keydown-ENTER', () => this.beginBattle());
    this.input.keyboard?.on('keydown-ONE', () => { this.speed = 1; });
    this.input.keyboard?.on('keydown-TWO', () => { this.speed = 2; });
    this.input.keyboard?.on('keydown-THREE', () => { this.speed = 3; });
  }

  private beginBattle() {
    if (this.phase !== 'deployment') return;
    this.phase = 'fighting';
    for (const formation of this.attackers) {
      if (formation.reserve) formation.setOrder('hold');
      else if (['ranged', 'gunpowder', 'artillery'].includes(formation.profile.role)) formation.setOrder('skirmish');
      else formation.setOrder('attack');
    }
    for (const formation of this.defenders) formation.setOrder('hold');
    this.statusText?.setText(`${this.battlefield.terrainName} • battle joined • capture objectives or break the enemy army`);
    AudioManager.instance.playUi(this, 'sfx_ui_action_bonang');
  }

  private stepBattle(delta: number) {
    if (this.phase !== 'fighting' || this.paused || this.resultShown) { this.refreshHud(); return; }
    const scaled = delta * this.speed;
    this.elapsedMs += scaled;
    this.defenderAI.update(scaled, this.defenders, this.attackers);
    const attackerAura = this.commanderAura('attacker');
    const defenderAura = this.commanderAura('defender');
    for (const formation of this.attackers) formation.update(delta, this.defenders, this.speed, attackerAura);
    for (const formation of this.defenders) formation.update(delta, this.attackers, this.speed, defenderAura);
    this.separateFormations(this.attackers);
    this.separateFormations(this.defenders);
    this.updateObjectivePressure(scaled);
    const resolution = resolveBattleWinner(this.attackers, this.defenders, this.objectiveWinner, this.elapsedMs >= 240000);
    if (resolution.winner) { this.endReason = resolution.reason ?? 'destroyed'; this.finish(resolution.winner); }
    this.refreshHud();
  }

  private updateObjectivePressure(delta: number) {
    let attackerControl = 0;
    let defenderControl = 0;
    for (const objective of this.battlefield.objectives) {
      const attackers = this.attackers.filter((formation) => formation.alive && !formation.routed && Phaser.Math.Distance.Between(formation.x, formation.y, objective.x, objective.y) <= objective.radius);
      const defenders = this.defenders.filter((formation) => formation.alive && !formation.routed && Phaser.Math.Distance.Between(formation.x, formation.y, objective.x, objective.y) <= objective.radius);
      if (attackers.length && !defenders.length) attackerControl++;
      if (defenders.length && !attackers.length) defenderControl++;
    }
    if (attackerControl > defenderControl) {
      this.objectivePressure.attacker += delta / 1000;
      this.objectivePressure.defender = Math.max(0, this.objectivePressure.defender - delta / 1800);
    } else if (defenderControl > attackerControl) {
      this.objectivePressure.defender += delta / 1000;
      this.objectivePressure.attacker = Math.max(0, this.objectivePressure.attacker - delta / 1800);
    } else {
      this.objectivePressure.attacker = Math.max(0, this.objectivePressure.attacker - delta / 2600);
      this.objectivePressure.defender = Math.max(0, this.objectivePressure.defender - delta / 2600);
    }
    const threshold = this.battlefield.objectives.length > 1 ? 28 : 22;
    if (this.objectivePressure.attacker >= threshold) this.objectiveWinner = 'attacker';
    if (this.objectivePressure.defender >= threshold) this.objectiveWinner = 'defender';
  }

  private issueOrder(order: BattleOrderType, point?: { x: number; y: number }, target?: Formation) {
    const formations = this.selected.size ? [...this.selected] : this.attackers.filter((formation) => formation.alive);
    if (this.phase === 'deployment' && !['reserve', 'rally', 'hold'].includes(order)) return;
    for (const formation of formations) formation.setOrder(order, point, target);
    if (order !== 'move' && order !== 'hold') AudioManager.instance.playUi(this, order === 'charge' ? 'sfx_battle_cavalry_charge' : 'sfx_ui_action_bonang');
    this.refreshHud();
  }

  private requestWithdrawal() {
    if (this.phase === 'deployment') { this.scene.start(SceneKeys.CampaignMap); return; }
    if (this.phase !== 'fighting') return;
    this.withdrawalRequested = true;
    for (const formation of this.attackers.filter((entry) => entry.alive)) formation.setOrder('withdraw');
  }

  private deploySelection(x: number, y: number) {
    const formations = this.selected.size ? [...this.selected] : this.attackers.filter((formation) => formation.alive && !formation.reserve);
    if (!formations.length) return;
    const columns = Math.ceil(Math.sqrt(formations.length));
    formations.forEach((formation, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const point = { x: x + (col - (columns - 1) / 2) * 95, y: y + (row - Math.floor(formations.length / columns) / 2) * 84 };
      if (this.battlefield.insideDeployment('attacker', point)) { formation.setPosition(point.x, point.y); formation.facePoint({ x: this.battlefield.bounds.centerX, y: this.battlefield.bounds.centerY }); }
    });
    this.refreshHud();
  }

  private rotateSelection(delta: number) {
    const formations = this.selected.size ? [...this.selected] : this.attackers.filter((formation) => formation.alive);
    for (const formation of formations) formation.rotate(delta);
    this.refreshHud();
  }

  private selectInitialLine() {
    this.clearSelection();
    for (const formation of this.attackers.filter((entry) => !entry.reserve).slice(0, 4)) { formation.setSelected(true); this.selected.add(formation); }
  }

  private clearSelection() { for (const formation of this.selected) formation.setSelected(false); this.selected.clear(); }

  private finish(winner: BattleSide) {
    if (this.phase === 'result' || this.resultShown) return;
    this.phase = 'result';
    this.resultShown = true;
    this.paused = true;
    const attackerFactionId = this.inputData?.attackerFactionId ?? 'attacker';
    const defenderFactionId = this.inputData?.defenderFactionId ?? 'defender';
    const winnerFactionId = winner === 'attacker' ? attackerFactionId : defenderFactionId;
    const loserFactionId = winner === 'attacker' ? defenderFactionId : attackerFactionId;
    const result: BattleResult = {
      winnerFactionId,
      loserFactionId,
      provinceId: this.inputData?.provinceId ?? GameState.instance.requireCampaign().selectedProvinceId ?? 'unknown',
      attackerLosses: this.sideLosses(this.attackers),
      defenderLosses: this.sideLosses(this.defenders),
      routed: this.endReason === 'routed' || this.endReason === 'withdrawn',
      terrainId: this.battlefield.terrainId,
      durationSeconds: Math.round(this.elapsedMs / 1000),
      attackerRemaining: this.attackers.reduce((sum, formation) => sum + formation.liveCount(), 0),
      defenderRemaining: this.defenders.reduce((sum, formation) => sum + formation.liveCount(), 0),
      attackerMorale: this.averageMorale(this.attackers),
      defenderMorale: this.averageMorale(this.defenders),
      objectiveWinner: this.objectiveWinner,
      withdrawal: this.withdrawalRequested ? 'attacker' : undefined,
      decisive: winner === 'attacker' ? sideScore(this.attackers) > sideScore(this.defenders) * 1.7 : sideScore(this.defenders) > sideScore(this.attackers) * 1.7,
    };
    ArmyManager.instance.applyBattleResult(result);
    GameState.instance.log(`Battle won by ${DataRegistry.instance.factions[winnerFactionId]?.name ?? winnerFactionId}.`, winner === 'attacker' ? 'success' : 'danger');
    this.showResult(result, winner);
  }

  private showResult(result: BattleResult, winner: BattleSide) {
    const { width, height } = this.scale;
    const winnerName = DataRegistry.instance.factions[result.winnerFactionId]?.name ?? result.winnerFactionId;
    const best = [...this.attackers, ...this.defenders].sort((a, b) => b.kills - a.kills)[0];
    this.add.rectangle(width / 2, height / 2, 720, 430, VisualTheme.colors.night, 0.985).setStrokeStyle(4, VisualTheme.colors.goldBright, 1).setDepth(3000);
    this.add.text(width / 2, height / 2 - 166, `${winner === 'attacker' ? 'Victory' : 'Defeat'} — ${winnerName}`, { fontFamily: VisualTheme.fonts.display, fontSize: '37px', color: VisualTheme.text.title, stroke: '#120b07', strokeThickness: 3 }).setOrigin(0.5).setDepth(3001);
    this.add.text(width / 2, height / 2 - 112, `${this.battlefield.terrainName} • ${this.endReason.replaceAll('_', ' ')} • ${result.durationSeconds}s`, { fontFamily: VisualTheme.fonts.body, fontSize: '16px', color: VisualTheme.text.accent }).setOrigin(0.5).setDepth(3001);
    this.add.text(width / 2, height / 2 - 60, `Attacker: ${result.attackerRemaining} remaining, ${result.attackerLosses} losses, morale ${result.attackerMorale}\nDefender: ${result.defenderRemaining} remaining, ${result.defenderLosses} losses, morale ${result.defenderMorale}\nObjective pressure: attacker ${Math.round(this.objectivePressure.attacker)}s • defender ${Math.round(this.objectivePressure.defender)}s\nDistinguished formation: ${best?.id ?? 'none'} (${best?.kills ?? 0} confirmed battlefield losses)`, { fontFamily: VisualTheme.fonts.body, fontSize: '17px', color: VisualTheme.text.body, align: 'center', lineSpacing: 10 }).setOrigin(0.5, 0).setDepth(3001);
    createTextButton(this, { label: 'Return to Campaign', x: width / 2, y: height / 2 + 155, width: 260, height: 46, fontSize: 16, onClick: () => this.scene.start(SceneKeys.CampaignMap) }).setDepth(3002);
  }

  private refreshHud() {
    if (!this.hudText) return;
    const selected = [...this.selected];
    const selectedText = selected.length ? selected.slice(0, 3).map((formation) => `${formation.id}: ${formation.statusText()}`).join('\n') : 'No formations selected.';
    this.hudText.setText(`${this.phase.toUpperCase()} • ${this.paused ? 'PAUSED' : `${this.speed}×`} • ${Math.floor(this.elapsedMs / 1000)}s • Friendly ${this.attackers.filter((formation) => formation.alive && !formation.withdrawn).length} • Enemy ${this.defenders.filter((formation) => formation.alive && !formation.withdrawn).length}\nObjective A ${Math.round(this.objectivePressure.attacker)}s / D ${Math.round(this.objectivePressure.defender)}s • Commander aura ${this.commanderAura('attacker').toFixed(1)}\n${selectedText}`);
    this.statusText?.setText(this.phase === 'deployment' ? `${this.battlefield.terrainName} • right-click inside the green deployment zone to reposition • Q/E changes facing • B marks reserves` : `${this.battlefield.terrainName} • terrain, cover, elevation, fatigue, ammunition, flanking and commander auras are active`);
    this.drawMinimap();
  }

  private drawMinimap() {
    this.children.list.filter((child: Phaser.GameObjects.GameObject) => child.name === 'minimap-dot' || child.name === 'minimap-objective').forEach((child: Phaser.GameObjects.GameObject) => child.destroy());
    const ox = this.scale.width - 197;
    const oy = 122;
    for (const formation of [...this.attackers, ...this.defenders]) {
      if (!formation.alive || formation.withdrawn) continue;
      const x = ox + (formation.x - this.battlefield.bounds.x) / this.battlefield.bounds.width * 160;
      const y = oy + (formation.y - this.battlefield.bounds.y) / this.battlefield.bounds.height * 82;
      const dot = this.add.rectangle(x, y, formation.profile.role === 'artillery' ? 7 : 5, 5, formation.side === 'attacker' ? 0xffdf70 : 0xff6f6f).setDepth(1002);
      dot.name = 'minimap-dot';
    }
    for (const objective of this.battlefield.objectives) {
      const x = ox + (objective.x - this.battlefield.bounds.x) / this.battlefield.bounds.width * 160;
      const y = oy + (objective.y - this.battlefield.bounds.y) / this.battlefield.bounds.height * 82;
      const marker = this.add.circle(x, y, 4, 0xffffff, 0.85).setDepth(1002);
      marker.name = 'minimap-objective';
    }
  }

  private deploymentPoint(side: BattleSide, index: number, count: number, role: BattleRole) {
    const zone = this.battlefield.deploymentZone(side);
    const columns = role === 'ranged' || role === 'gunpowder' || role === 'artillery' ? 2 : 3;
    const row = index % Math.max(1, Math.min(6, Math.ceil(count / columns)));
    const depth = Math.floor(index / Math.max(1, Math.ceil(count / columns)));
    const xOffset = role === 'artillery' ? 42 : role === 'ranged' || role === 'gunpowder' ? 88 : 150 + depth * 78;
    const x = side === 'attacker' ? zone.x + xOffset : zone.right - xOffset;
    const y = zone.y + 52 + row * Math.min(92, (zone.height - 90) / Math.max(1, Math.min(5, count - 1)));
    return { x, y: Phaser.Math.Clamp(y, zone.y + 30, zone.bottom - 30) };
  }

  private profileFor(unit: UnitData): FormationCombatProfile {
    const role = this.roleFor(unit);
    const ranged = role === 'ranged' || role === 'gunpowder' || role === 'artillery';
    const projectile = role === 'artillery' ? 'cannon' : role === 'gunpowder' ? 'shot' : role === 'ranged' ? 'arrow' : 'none';
    return {
      role,
      attack: Math.max(1, unit.attack),
      defense: Math.max(1, unit.defense),
      speed: Math.max(18, unit.speed),
      maxRange: ranged ? Phaser.Math.Clamp(unit.range || (role === 'artillery' ? 360 : 230), 150, role === 'artillery' ? 460 : 340) : role === 'cavalry' ? 72 : role === 'elephant' ? 82 : 58,
      meleeRange: role === 'cavalry' ? 72 : role === 'elephant' ? 84 : role === 'artillery' ? 72 : 58,
      ammo: ranged ? Math.max(1, unit.ammo || (role === 'artillery' ? 8 : 12)) : 0,
      reloadMs: role === 'artillery' ? 3300 : role === 'gunpowder' ? 2100 : role === 'ranged' ? 1250 : 900,
      shock: role === 'elephant' ? 10 : role === 'cavalry' ? 7 : role === 'melee' && unit.defense >= 7 ? 4 : 2,
      armor: role === 'elephant' ? 6 : role === 'cavalry' ? 3 : role === 'spear' ? 2 : role === 'artillery' ? 1 : Math.max(0, Math.round(unit.defense / 3)),
      projectile,
    };
  }

  private roleFor(unit: UnitData): BattleRole {
    const category = `${unit.category} ${unit.name}`.toLowerCase();
    if (category.includes('elephant')) return 'elephant';
    if (category.includes('cannon') || category.includes('artillery') || category.includes('bombard')) return 'artillery';
    if (category.includes('cavalry') || category.includes('horse') || category.includes('mounted')) return 'cavalry';
    if (category.includes('gunpowder') || category.includes('musketeer') || category.includes('arquebus')) return 'gunpowder';
    if (category.includes('ranged') || category.includes('archer') || category.includes('bow')) return 'ranged';
    if (category.includes('spear') || category.includes('pike')) return 'spear';
    return 'melee';
  }

  private textureFor(factionId: string, unitId: string) {
    const unit = DataRegistry.instance.units[unitId];
    const role = unit ? this.roleFor(unit) : 'melee';
    const candidates: string[] = [];
    if (role === 'cavalry') candidates.push(`spritesheet_${factionId}_cavalry`);
    if (role === 'ranged') candidates.push(`spritesheet_${factionId}_archer`);
    if (role === 'gunpowder') candidates.push(`spritesheet_${factionId}_musketeer`);
    if (role === 'artillery') candidates.push(`spritesheet_${factionId}_cannon`);
    if (role === 'elephant') candidates.push(`spritesheet_${factionId}_war_elephant`, 'spritesheet_majapahit_war_elephant');
    candidates.push(`spritesheet_${factionId}_infantry`);
    return candidates.find((key) => this.textures.exists(key)) ?? (factionId === this.inputData?.attackerFactionId ? 'fallback.soldier.green' : 'fallback.soldier.red');
  }

  private commanderAura(side: BattleSide) {
    const campaign = GameState.instance.requireCampaign();
    const armyIds = side === 'attacker' ? this.inputData?.attackerArmyIds ?? [] : this.inputData?.defenderArmyIds ?? [];
    const commanders = armyIds.map((armyId) => campaign.armies[armyId]?.commanderId).filter((id): id is string => Boolean(id)).map((id) => campaign.commanders[id]).filter(Boolean);
    return commanders.length ? commanders.reduce((sum, commander) => sum + commander.martial * 0.7 + commander.loyalty * 0.03, 0) / commanders.length : 0;
  }

  private separateFormations(formations: Formation[]) {
    const active = formations.filter((formation) => formation.alive && !formation.withdrawn);
    for (let leftIndex = 0; leftIndex < active.length; leftIndex++) for (let rightIndex = leftIndex + 1; rightIndex < active.length; rightIndex++) {
      const left = active[leftIndex];
      const right = active[rightIndex];
      const dx = right.x - left.x;
      const dy = right.y - left.y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      if (distance >= 56) continue;
      const push = (56 - distance) * 0.08;
      left.setPosition(left.x - dx / distance * push, left.y - dy / distance * push);
      right.setPosition(right.x + dx / distance * push, right.y + dy / distance * push);
    }
  }

  private sideLosses(formations: Formation[]) { return formations.reduce((sum, formation) => sum + Math.max(0, formation.startingSoldiers - formation.liveCount()), 0); }
  private averageMorale(formations: Formation[]) { const active = formations.filter((formation) => formation.liveCount() > 0); return active.length ? Math.round(active.reduce((sum, formation) => sum + formation.morale, 0) / active.length) : 0; }

  private fallbackUnit(id: string): UnitData {
    return { id, name: id.replaceAll('_', ' '), factionTags: [], category: 'infantry', manpower: 100, attack: 4, defense: 4, morale: 45, speed: 42, range: 40, ammo: 0, supply: 1, costGold: 12, upkeepGold: 1, assetKey: id };
  }

  private renderBattleToText() {
    return JSON.stringify({ mode: 'battle', phase: this.phase, paused: this.paused, speed: this.speed, timeSeconds: Math.floor(this.elapsedMs / 1000), terrain: this.battlefield.terrainId, objectives: this.battlefield.objectives, objectivePressure: this.objectivePressure, coordinates: 'origin top-left; x increases right; y increases down', selected: [...this.selected].map((formation) => ({ id: formation.id, role: formation.profile.role, order: formation.order, x: Math.round(formation.x), y: Math.round(formation.y), facing: Math.round(Phaser.Math.RadToDeg(formation.facing)), morale: Math.round(formation.morale), cohesion: Math.round(formation.cohesion), fatigue: Math.round(formation.fatigue), ammo: formation.ammo, soldiers: formation.liveCount(), terrain: formation.lastTerrain.name })), attackers: this.attackers.map((formation) => this.formationSnapshot(formation)), defenders: this.defenders.map((formation) => this.formationSnapshot(formation)) });
  }

  private formationSnapshot(formation: Formation) {
    return { id: formation.id, role: formation.profile.role, alive: formation.alive, routed: formation.routed, withdrawn: formation.withdrawn, reserve: formation.reserve, x: Math.round(formation.x), y: Math.round(formation.y), morale: Math.round(formation.morale), cohesion: Math.round(formation.cohesion), fatigue: Math.round(formation.fatigue), ammo: formation.ammo, soldiers: formation.liveCount(), order: formation.order, kills: formation.kills };
  }
}
